/**
 * taskApi.js
 *
 * The task board used to call 15 custom PHP actions. This replaces all of
 * them with your four generic CRUD helpers - addData, getData, updateData
 * and deleteData - so nothing new has to live on the server.
 *
 * It deliberately keeps the SAME call signature the components already use:
 *
 *     const callAction = useTaskApi();
 *     await callAction("proccess.php", "getTaskWorkspace", { owner_id: 12 });
 *
 * so Dashboard.js and TaskSideBar.js only swap where callAction comes from.
 *
 * ---------------------------------------------------------------------
 * WHAT THE GENERIC CRUD CANNOT DO, AND HOW THIS WORKS AROUND IT
 * ---------------------------------------------------------------------
 *
 * 1. getData(table, id) filters by `staff_id` for any table that is not in
 *    its special list, and tasks_V2 has no staff_id column. So every read
 *    here fetches the WHOLE table with getData(endpoint, table) and filters
 *    in JavaScript.
 *
 * 2. addData returns `SELECT * FROM <table>` - the whole table, with no
 *    insert id. So after every insert we locate the new row by a natural
 *    key (task_code, or owner+parent+name for folders, and so on).
 *
 * 3. addData and updateData both SKIP any value that is '' or null. That
 *    means a column can be set but never cleared. Sentinel 0 is used for
 *    "no folder" and "no parent" instead of NULL, which is why the schema
 *    defines those columns NOT NULL DEFAULT 0.
 *
 * 4. There is no server-side permission check any more. The view/edit/full
 *    rules below run in the browser: they keep the UI honest, but they are
 *    not a security boundary. Anyone who can call the endpoint can read
 *    every row in these tables, including tasks marked private.
 */

import { useCallback, useMemo } from "react";
import { useAuth } from "../../AuthContext";

/* -------------------------------------------------------------------- */
/* Tables                                                               */
/* -------------------------------------------------------------------- */

const T = {
  tasks: "tasks_V2",
  members: "task_members_V2",
  subtasks: "task_subtasks_V2",
  subtaskMembers: "task_subtask_members_V2",
  progress: "task_progress_V2",
  comments: "task_comments_V2",
  attachments: "task_attachments_V2",
  folders: "task_folders_V2",
  access: "task_access_V2",
  staffs: "staffs",
};

/**
 * Column whitelists. addData builds its INSERT from whatever keys you hand
 * it, so an unknown key is an SQL error. The forms carry UI-only fields, so
 * everything is filtered through these before it goes near the database.
 *
 * completed_at / completed_by / createDate / last_updated are deliberately
 * absent - the rollup cron owns those.
 */
const COLUMNS = {
  [T.tasks]: [
    "task_code", "visibility", "title", "description", "department",
    "priority", "status", "start_date", "deadline", "progress_percent",
    "created_by", "origin", "owner_id", "folder_id",
  ],
  [T.members]: ["task_id", "staff_id", "role_on_task", "assigned_by"],
  [T.subtasks]: [
    "task_id", "title", "description", "priority", "status",
    "start_date", "deadline", "progress_percent", "sort_order", "created_by",
  ],
  [T.subtaskMembers]: ["subtask_id", "staff_id", "is_owner", "assigned_by"],
  [T.progress]: [
    "task_id", "subtask_id", "staff_id", "log_date", "hours_spent",
    "progress_percent", "work_done", "blockers", "work_mode",
  ],
  [T.comments]: ["task_id", "subtask_id", "staff_id", "comment"],
  // image / document are not really columns we fill - addData writes the
  // stored filename into whichever one the file was posted under.
  [T.attachments]: [
    "task_id", "subtask_id", "comment_id", "staff_id",
    "file_name", "file_path", "file_type", "file_size",
  ],
  [T.folders]: [
    "owner_id", "parent_id", "name", "color", "sort_order",
    "is_system", "created_by",
  ],
  [T.access]: ["owner_id", "grantee_id", "permission", "granted_by"],
};

/* -------------------------------------------------------------------- */
/* Small helpers                                                        */
/* -------------------------------------------------------------------- */

const ok = (data = null, message = "") => ({ success: true, data, message });
const fail = (message) => ({ success: false, data: null, message });

const same = (a, b) => String(a ?? "") === String(b ?? "");

/** Blank, null or 0 all mean "not set". */
const unset = (v) =>
  v === null || v === undefined || String(v).trim() === "" || Number(v) === 0;

/**
 * Which account a task is filed under. owner_id arrived with sharing.sql, so
 * rows created before it fall back to whoever raised the task.
 *
 * Do not shorten this to `t.owner_id || t.created_by`: MySQL hands these
 * back as STRINGS, and "0" is truthy in JavaScript, so a zeroed owner_id
 * would silently win and the task would belong to nobody.
 */
const taskOwnerId = (t) => (unset(t?.owner_id) ? t?.created_by : t?.owner_id);

/** Keeps only real columns, and drops blanks the CRUD would skip anyway. */
const pick = (table, payload) => {
  const allowed = COLUMNS[table] || [];
  const out = {};
  allowed.forEach((key) => {
    const value = payload[key];
    if (value === undefined || value === null) return;
    const asString = typeof value === "string" ? value.trim() : value;
    if (asString === "") return;
    out[key] = asString;
  });
  return out;
};

/**
 * Where uploads land. addData() decides the folder from the file's actual
 * type, NOT from the FormData key, so an image posted as `document` still
 * ends up in the pictures folder. Keep these in step with the $fileTypeMap
 * paths in functions.php.
 */
export const UPLOAD_BASE = {
  image: "https://mindsinaction.com.na/assets/media/pictures/",
  document: "https://mindsinaction.com.na/assets/document/",
};

/** Which of addData's two upload slots a file belongs in. */
export const uploadSlot = (file) =>
  String(file?.type || "").startsWith("image/") ? "image" : "document";

const isActive = (staff) =>
  ["active", "1"].includes(String(staff?.status || "").toLowerCase().trim());

const staffName = (staff) =>
  staff ? `${staff.FirstName || ""} ${staff.LastName || ""}`.trim() : "";

/** view < edit < full */
const RANK = { view: 1, edit: 2, full: 3 };
const atLeast = (permission, needed) =>
  (RANK[permission] || 0) >= (RANK[needed] || 0);

/* -------------------------------------------------------------------- */

export const useTaskApi = () => {
  const { addData, getData, updateData, deleteData, userID } = useAuth();
  const me = String(userID ?? "");

  /* ---------------- reads ---------------- */

  const readAll = useCallback(
    async (endpoint, table) => {
      const res = await getData(endpoint, table);
      return res?.success && Array.isArray(res.data) ? res.data : [];
    },
    [getData]
  );

  /**
   * Inserts, then finds the row again by natural key, because addData
   * returns the whole table rather than an insert id.
   */
  const insertAndFind = useCallback(
    async (endpoint, table, payload, matcher) => {
      const res = await addData(endpoint, table, pick(table, payload));
      if (!res?.success) {
        return { row: null, message: res?.message || "Insert failed" };
      }
      const rows = Array.isArray(res.data) ? res.data : [];
      const found = rows.filter(matcher);
      if (!found.length) {
        // The insert reported success but the row is not in what came back.
        return { row: null, message: "Saved, but the new record could not be located" };
      }
      // Newest wins if the natural key somehow matched more than one.
      const row = found.reduce((a, b) => (Number(b.id) > Number(a.id) ? b : a));
      return { row, message: "" };
    },
    [addData]
  );

  /** My permission on a given account, and whether it is my own. */
  const resolveAccess = useCallback(
    async (endpoint, ownerId) => {
      if (same(ownerId, me)) return { permission: "full", isOwn: true };
      const access = await readAll(endpoint, T.access);
      const row = access.find(
        (a) => same(a.owner_id, ownerId) && same(a.grantee_id, me)
      );
      return {
        permission: row ? row.permission : null,
        isOwn: false,
      };
    },
    [readAll, me]
  );

  /**
   * The same check, but starting from a task rather than an account. Every
   * write below goes through this: the buttons are already hidden in the UI,
   * but hiding a button is not a rule, and the board is not the only thing
   * that can call these.
   *
   *   view  - read only
   *   edit  - may change what already exists
   *   full  - may also create and delete
   */
  const permissionForTask = useCallback(
    async (endpoint, taskId) => {
      const [tasks, members] = await Promise.all([
        readAll(endpoint, T.tasks),
        readAll(endpoint, T.members),
      ]);
      const task = tasks.find((t) => same(t.id, taskId));
      if (!task) return { task: null, permission: null, isMember: false };

      const ownerId = taskOwnerId(task);
      const { permission } = await resolveAccess(endpoint, ownerId);

      /**
       * Account permission is about browsing someone else's board. Being
       * ON a task is different: it is your work, wherever it was raised.
       * Without this, a task assigned to you from an account you cannot
       * otherwise see would be unopenable - the card would appear on your
       * board and then refuse to open.
       *
       * Membership lets you DO the work (comment, log progress, tick your
       * subtasks). It does not let you rewrite the brief or bring other
       * people in - those stay with the account.
       */
      const isMember = members.some(
        (m) => same(m.task_id, taskId) && same(m.staff_id, me)
      );

      return { task, permission, isMember };
    },
    [readAll, resolveAccess, me]
  );

  const DENIED = {
    edit: "You may only view this task account",
    full: "You need full rights to do that here",
  };

  /**
   * The rules, in one place.
   *
   *   view  - read only. Nothing else, not even a comment.
   *   edit  - change the task, its subtasks, and comment
   *   full  - also create subtasks, bring people in, log daily progress
   *
   * Membership is a fallback, NOT an override. Someone assigned a task in
   * an account they have no relationship with still has to be able to work
   * on it - otherwise the card appears on their board and does nothing.
   *
   * But an explicit "view only" grant is a deliberate statement about this
   * account, so it wins: a view-only person stays read-only even on a task
   * they are named on.
   */
  const mayWork = (permission, isMember) =>
    atLeast(permission, "edit") || (isMember && !permission);

  const mayManage = (permission, isMember) =>
    atLeast(permission, "full") || (isMember && !permission);

  /* ---------------- task codes ---------------- */

  const nextTaskCode = (tasks) => {
    let highest = 0;
    tasks.forEach((t) => {
      const match = String(t.task_code || "").match(/(\d+)\s*$/);
      if (match) highest = Math.max(highest, parseInt(match[1], 10));
    });
    return `TSK${String(highest + 1).padStart(6, "0")}`;
  };

  /* ==================================================================== */
  /* The actions                                                          */
  /* ==================================================================== */

  const getTaskAccounts = useCallback(
    async (endpoint) => {
      if (!me) return fail("Not signed in");

      const [staffs, access] = await Promise.all([
        readAll(endpoint, T.staffs),
        readAll(endpoint, T.access),
      ]);

      const mine = staffs.find((s) => same(s.Id, me));
      const rows = [];

      if (mine) {
        rows.push({
          owner_id: mine.Id,
          owner_name: staffName(mine),
          owner_image: mine.image || "",
          owner_department: mine.department || "",
          owner_role: mine.role || "",
          permission: "full",
          is_own: 1,
          granted_at: null,
        });
      }

      access
        .filter((a) => same(a.grantee_id, me))
        .forEach((a) => {
          const owner = staffs.find((s) => same(s.Id, a.owner_id));
          if (!owner || !isActive(owner)) return;
          rows.push({
            owner_id: owner.Id,
            owner_name: staffName(owner),
            owner_image: owner.image || "",
            owner_department: owner.department || "",
            owner_role: owner.role || "",
            permission: a.permission || "view",
            is_own: 0,
            granted_at: a.granted_at || null,
          });
        });

      rows.sort((a, b) => {
        if (a.is_own !== b.is_own) return b.is_own - a.is_own;
        return String(a.owner_name).localeCompare(String(b.owner_name));
      });

      return ok(rows);
    },
    [readAll, me]
  );

  const getTaskWorkspace = useCallback(
    async (endpoint, payload) => {
      const ownerId = payload.owner_id;
      if (!ownerId) return fail("No task account selected");

      const { permission, isOwn } = await resolveAccess(endpoint, ownerId);
      if (!permission) return fail("You do not have access to this task account");

      // Whose board this is, so the header can say so rather than just "Tasks".
      const allStaffs = await readAll(endpoint, T.staffs);
      const owner = allStaffs.find((s) => same(s.Id, ownerId));
      const ownerInfo = {
        owner_id: ownerId,
        owner_name: staffName(owner),
        owner_image: owner?.image || "",
        owner_department: owner?.department || "",
      };

      const allFolders = await readAll(endpoint, T.folders);
      const folders = allFolders
        .filter((f) => same(f.owner_id, ownerId))
        .sort(
          (a, b) =>
            Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
            String(a.name).localeCompare(String(b.name))
        );

      if (Number(payload.folders_only) === 1) {
        return ok({ ...ownerInfo, permission, is_own: isOwn ? 1 : 0, folders });
      }

      const [allTasks, allSubtasks, allMembers, allProgress] = await Promise.all([
        readAll(endpoint, T.tasks),
        readAll(endpoint, T.subtasks),
        readAll(endpoint, T.members),
        readAll(endpoint, T.progress),
      ]);

      // Everything filed in this account. Access to the account IS the
      // permission to see what is in it - that is what granting access
      // means. private/public decides who can find a task WITHOUT account
      // access; it must not also filter people who have been given it, or
      // sharing an account would show the person nothing (tasks default to
      // private, so that was exactly what happened).
      const filedHere = allTasks.filter((t) => same(taskOwnerId(t), ownerId));

      /**
       * Work assigned to you shows on YOUR board, wherever it was raised.
       * Being put on a task in someone else's account is how work reaches
       * you, so it has to surface somewhere you actually look.
       *
       * These carry is_foreign so the board can label them and stop you
       * re-filing them: they live in the other person's folders, and moving
       * one would make it vanish from their board.
       */
      /**
       * A board shows its owner's whole workload, not just what was raised
       * in their own account. Work reaches people by being put on a task in
       * someone else's account, so those tasks belong here too - and that
       * is as true of a colleague's board you have access to as it is of
       * your own.
       */
      let tasks = filedHere;
      {
        const theirs = new Set(
          allMembers
            .filter((m) => same(m.staff_id, ownerId))
            .map((m) => String(m.task_id))
        );
        const here = new Set(filedHere.map((t) => String(t.id)));

        /**
         * Folders are per-account, so the id a task carries means nothing on
         * THIS board. Match on NAME instead: a task sitting in the other
         * account's "To do" belongs in this board's "To do", not dumped
         * in Unfiled.
         *
         * folder_id is rewritten to this board's folder purely so the
         * cards can be placed. The real filing is kept as origin_folder_*,
         * and because these cards are not draggable it is never written
         * back.
         */
        const folderHereByName = new Map(
          folders.map((f) => [String(f.name || "").trim().toLowerCase(), f])
        );

        const assignedElsewhere = allTasks
          .filter((t) => theirs.has(String(t.id)) && !here.has(String(t.id)))
          .map((t) => {
            const from = allStaffs.find((s) => same(s.Id, taskOwnerId(t)));
            const theirFolder = allFolders.find((f) => same(f.id, t.folder_id));
            const equivalent = theirFolder
              ? folderHereByName.get(
                  String(theirFolder.name || "").trim().toLowerCase()
                )
              : null;

            return {
              ...t,
              folder_id: equivalent ? equivalent.id : "0",
              origin_folder_id: t.folder_id,
              origin_folder_name: theirFolder?.name || "",
              is_foreign: 1,
              from_owner_id: taskOwnerId(t),
              from_owner_name: staffName(from),
            };
          });

        tasks = [...filedHere, ...assignedElsewhere];
      }

      const taskIds = new Set(tasks.map((t) => String(t.id)));
      const forTasks = (rows) => rows.filter((r) => taskIds.has(String(r.task_id)));

      return ok({
        ...ownerInfo,
        permission,
        is_own: isOwn ? 1 : 0,
        folders,
        tasks,
        subtasks: forTasks(allSubtasks).sort(
          (a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0)
        ),
        members: forTasks(allMembers),
        progress: forTasks(allProgress),
      });
    },
    [readAll, resolveAccess, me]
  );

  const getTaskAccessList = useCallback(
    async (endpoint, payload) => {
      const ownerId = payload.owner_id || me;
      const [access, staffs] = await Promise.all([
        readAll(endpoint, T.access),
        readAll(endpoint, T.staffs),
      ]);

      const rows = access
        .filter((a) => same(a.owner_id, ownerId))
        .map((a) => {
          const grantee = staffs.find((s) => same(s.Id, a.grantee_id));
          return {
            ...a,
            grantee_name: staffName(grantee),
            grantee_image: grantee?.image || "",
            grantee_department: grantee?.department || "",
            grantee_role: grantee?.role || "",
          };
        })
        .sort((a, b) => String(a.grantee_name).localeCompare(String(b.grantee_name)));

      return ok(rows);
    },
    [readAll, me]
  );

  /**
   * Everything the single-task page needs, in one call: the task itself,
   * the folder it is filed in, the people on it, its subtasks and every
   * progress log - each already joined to the staff record, so the page
   * does not have to fetch staffs separately.
   */
  const getTaskDetail = useCallback(
    async (endpoint, payload) => {
      const taskId = payload.id;
      if (!taskId) return fail("No task given");

      const [tasks, staffs] = await Promise.all([
        readAll(endpoint, T.tasks),
        readAll(endpoint, T.staffs),
      ]);

      const task = tasks.find((t) => same(t.id, taskId));
      if (!task) return fail("That task no longer exists");

      const ownerId = taskOwnerId(task);
      const { permission, isOwn } = await resolveAccess(endpoint, ownerId);

      const [
        allSubtasks,
        allMembers,
        allProgress,
        folders,
        subtaskMembers,
        allComments,
        allAttachments,
      ] = await Promise.all([
        readAll(endpoint, T.subtasks),
        readAll(endpoint, T.members),
        readAll(endpoint, T.progress),
        readAll(endpoint, T.folders),
        readAll(endpoint, T.subtaskMembers),
        readAll(endpoint, T.comments),
        readAll(endpoint, T.attachments),
      ]);

      /**
       * Turn a row into something the UI can render directly. The stored
       * filename lives in `image` or `document` depending on how it was
       * uploaded, and the folder follows the file's real type - so read the
       * slot from whichever column is filled, not from the key we sent.
       */
      const attachments = allAttachments
        .filter((a) => same(a.task_id, taskId))
        .map((a) => {
          const stored = a.image || a.document || "";
          const isImage = Boolean(a.image);
          return {
            ...a,
            is_image: isImage ? 1 : 0,
            url: stored
              ? `${UPLOAD_BASE[isImage ? "image" : "document"]}${stored}`
              : "",
          };
        });

      const members = allMembers.filter((m) => same(m.task_id, taskId));
      const isMember = members.some((m) => same(m.staff_id, me));

      /**
       * Two ways in: the account is open to you, or you are on the task.
       * The second matters because a task assigned to you from an account
       * you cannot otherwise see appears on your board - and a card that
       * refuses to open would be worse than not showing it at all.
       *
       * private/public is deliberately NOT re-checked here: account access
       * already answered that question, and checking twice locked grantees
       * out of the very tasks they were given access to.
       */
      if (!permission && !isMember) {
        return fail("You do not have access to this task");
      }

      const withStaff = (row, idField) => {
        const staff = staffs.find((s) => same(s.Id, row[idField]));
        return {
          ...row,
          staff_name: staffName(staff),
          staff_first: staff?.FirstName || "",
          staff_last: staff?.LastName || "",
          staff_image: staff?.image || "",
          staff_department: staff?.department || "",
          staff_role: staff?.role || "",
        };
      };

      const subtasks = allSubtasks
        .filter((s) => same(s.task_id, taskId))
        .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0))
        .map((s) => {
          const owner = subtaskMembers.find(
            (sm) => same(sm.subtask_id, s.id) && Number(sm.is_owner) === 1
          );
          const staff = owner && staffs.find((st) => same(st.Id, owner.staff_id));
          return {
            ...s,
            owner_staff_id: owner ? owner.staff_id : "",
            owner_name: staffName(staff),
            owner_image: staff?.image || "",
          };
        });

      const author = staffs.find((s) => same(s.Id, task.created_by));
      const accountOwner = staffs.find((s) => same(s.Id, ownerId));

      return ok({
        task,
        // Raw, so the page can tell "granted view only" apart from "no
        // account relationship at all". Those two mean different things.
        permission: permission || "",
        is_member: isMember ? 1 : 0,
        is_own: isOwn ? 1 : 0,
        folder: folders.find((f) => same(f.id, task.folder_id)) || null,
        // Every folder in this account, so the edit form can offer a move.
        folders: folders
          .filter((f) => same(f.owner_id, ownerId))
          .sort(
            (a, b) =>
              Number(a.sort_order || 0) - Number(b.sort_order || 0) ||
              String(a.name).localeCompare(String(b.name))
          ),
        members: members.map((m) => withStaff(m, "staff_id")),
        subtasks,
        progress: allProgress
          .filter((p) => same(p.task_id, taskId))
          .sort((a, b) => String(b.log_date).localeCompare(String(a.log_date)))
          .map((p) => withStaff(p, "staff_id")),
        // Oldest first - a thread reads top to bottom.
        comments: allComments
          .filter((c) => same(c.task_id, taskId))
          .sort((a, b) => String(a.createDate).localeCompare(String(b.createDate)))
          .map((c) => ({
            ...withStaff(c, "staff_id"),
            attachments: attachments.filter((a) => same(a.comment_id, c.id)),
          })),
        // Anything attached to the task rather than to a comment.
        attachments: attachments.filter((a) => unset(a.comment_id)),
        author_name: staffName(author),
        author_image: author?.image || "",
        owner_id: ownerId,
        owner_name: staffName(accountOwner),
      });
    },
    [readAll, resolveAccess, me]
  );

  const saveTask = useCallback(
    async (endpoint, payload) => {
      const ownerId = payload.owner_id || me;
      const { permission } = await resolveAccess(endpoint, ownerId);
      if (!atLeast(permission, "edit")) {
        return fail("You may only view this task account");
      }

      if (!String(payload.title || "").trim()) return fail("Title is required");
      // start_date and deadline are NOT NULL with no default, and the CRUD
      // drops blanks, so an empty one would fail deep in SQL instead of here.
      if (!payload.start_date) return fail("Start date is required");
      if (!payload.deadline) return fail("Deadline is required");
      if (String(payload.deadline) < String(payload.start_date)) {
        return fail("The deadline cannot be before the start date");
      }

      /* ---- update ---- */
      if (payload.id) {
        const res = await updateData(
          endpoint,
          T.tasks,
          pick(T.tasks, payload),
          payload.id
        );
        if (!res?.success) return fail(res?.message || "Could not update the task");
        return ok({ id: payload.id, task_code: payload.task_code || "" });
      }

      /* ---- create ---- */
      if (!atLeast(permission, "full") && !same(ownerId, me)) {
        return fail("You need full rights to create tasks in this account");
      }

      const existing = await readAll(endpoint, T.tasks);
      const code = nextTaskCode(existing);

      const { row, message } = await insertAndFind(
        endpoint,
        T.tasks,
        {
          ...payload,
          task_code: code,
          owner_id: ownerId,
          created_by: me,
          folder_id: payload.folder_id || "0",
          status: payload.status || "active",
          visibility: payload.visibility || "private",
          priority: payload.priority || "normal",
        },
        (r) => same(r.task_code, code)
      );

      if (!row) return fail(message);
      return ok({ id: row.id, task_code: row.task_code }, "Task created");
    },
    [resolveAccess, readAll, insertAndFind, updateData, me]
  );

  const saveTaskMember = useCallback(
    async (endpoint, payload) => {
      const { task_id, staff_id, role_on_task } = payload;
      if (!task_id || !staff_id) return fail("Task and staff member are required");

      const { permission } = await permissionForTask(endpoint, task_id);
      if (!atLeast(permission, "full")) return fail(DENIED.full);

      const members = await readAll(endpoint, T.members);
      const existing = members.find(
        (m) => same(m.task_id, task_id) && same(m.staff_id, staff_id)
      );

      if (existing) {
        if (same(existing.role_on_task, role_on_task)) return ok({ id: existing.id });
        const res = await updateData(
          endpoint,
          T.members,
          { role_on_task },
          existing.id
        );
        return res?.success
          ? ok({ id: existing.id })
          : fail(res?.message || "Could not update the assignment");
      }

      const { row, message } = await insertAndFind(
        endpoint,
        T.members,
        { task_id, staff_id, role_on_task: role_on_task || "collaborator", assigned_by: me },
        (r) => same(r.task_id, task_id) && same(r.staff_id, staff_id)
      );
      return row ? ok({ id: row.id }) : fail(message);
    },
    [readAll, insertAndFind, updateData, permissionForTask, me]
  );

  const saveSubtask = useCallback(
    async (endpoint, payload) => {
      /* ---- update ---- */
      if (payload.id) {
        // task_id is not always sent on an update, so fall back to the row.
        let taskId = payload.task_id;
        if (!taskId) {
          const rows = await readAll(endpoint, T.subtasks);
          taskId = rows.find((s) => same(s.id, payload.id))?.task_id;
        }
        const { permission, isMember } = await permissionForTask(endpoint, taskId);
        if (!mayWork(permission, isMember)) return fail(DENIED.edit);

        const res = await updateData(
          endpoint,
          T.subtasks,
          pick(T.subtasks, payload),
          payload.id
        );
        if (!res?.success) {
          return fail(res?.message || "Could not update the subtask");
        }

        /**
         * Who is accountable lives in its own table, so a change here means
         * clearing the old owner row before writing the new one. Only touch
         * it when the caller actually passed the field - a status tick must
         * not wipe the owner.
         */
        if (payload.owner_staff_id !== undefined) {
          const rows = await readAll(endpoint, T.subtaskMembers);
          const current = rows.filter(
            (r) => same(r.subtask_id, payload.id) && Number(r.is_owner) === 1
          );

          const wanted = String(payload.owner_staff_id || "");
          const alreadyRight =
            current.length === 1 && same(current[0].staff_id, wanted);

          if (!alreadyRight) {
            for (const row of current) {
              await deleteData(endpoint, T.subtaskMembers, row.id);
            }
            if (wanted) {
              await addData(
                endpoint,
                T.subtaskMembers,
                pick(T.subtaskMembers, {
                  subtask_id: payload.id,
                  staff_id: wanted,
                  is_owner: 1,
                  assigned_by: me,
                })
              );
            }
          }
        }

        return ok({ id: payload.id }, "Subtask updated");
      }

      /* ---- create ---- */
      if (!payload.task_id) return fail("The subtask needs a parent task");
      if (!String(payload.title || "").trim()) return fail("Subtask title is required");

      // Adding a subtask is creating work, so it needs full rights.
      const { permission } = await permissionForTask(endpoint, payload.task_id);
      if (!atLeast(permission, "full")) return fail(DENIED.full);

      const before = await readAll(endpoint, T.subtasks);
      const beforeIds = new Set(before.map((s) => String(s.id)));

      const { row, message } = await insertAndFind(
        endpoint,
        T.subtasks,
        { ...payload, created_by: me, status: payload.status || "active" },
        (r) =>
          !beforeIds.has(String(r.id)) &&
          same(r.task_id, payload.task_id) &&
          same(r.title, String(payload.title).trim())
      );
      if (!row) return fail(message);

      // "Accountable for this subtask" lives in its own table.
      if (payload.owner_staff_id) {
        await addData(
          endpoint,
          T.subtaskMembers,
          pick(T.subtaskMembers, {
            subtask_id: row.id,
            staff_id: payload.owner_staff_id,
            is_owner: 1,
            assigned_by: me,
          })
        );
      }

      return ok({ id: row.id }, "Subtask added");
    },
    [readAll, insertAndFind, updateData, addData, deleteData, permissionForTask, me]
  );

  const saveTaskComment = useCallback(
    async (endpoint, payload) => {
      const body = String(payload.comment || "").trim();
      if (!body) return fail("Write something first");
      if (!payload.task_id) return fail("No task given");
      const { permission, isMember } = await permissionForTask(endpoint, payload.task_id);
      if (!mayWork(permission, isMember)) return fail(DENIED.edit);

      const before = await readAll(endpoint, T.comments);
      const beforeIds = new Set(before.map((c) => String(c.id)));

      const { row, message } = await insertAndFind(
        endpoint,
        T.comments,
        {
          task_id: payload.task_id,
          subtask_id: String(payload.subtask_id || "0"),
          staff_id: me,
          comment: body,
        },
        (r) =>
          !beforeIds.has(String(r.id)) &&
          same(r.task_id, payload.task_id) &&
          same(r.staff_id, me)
      );
      return row ? ok({ id: row.id }, "Comment posted") : fail(message);
    },
    [readAll, insertAndFind, permissionForTask, me]
  );

  /**
   * Uploads one file and records it against the task, and optionally
   * against a comment.
   *
   * The file rides in the SAME addData call as the row: PHP moves it out of
   * $_FILES and writes the stored filename into the `image` or `document`
   * column, so there is no separate upload step to go wrong halfway.
   */
  const saveTaskAttachment = useCallback(
    async (endpoint, payload) => {
      const file = payload.file;
      if (!file) return fail("No file given");
      if (!payload.task_id) return fail("No task given");

      // 10MB is addData's own ceiling; catching it here gives a sane
      // message instead of a rejected upload.
      if (file.size > 10 * 1024 * 1024) {
        return fail("That file is larger than 10MB");
      }

      const { permission, isMember } = await permissionForTask(endpoint, payload.task_id);
      if (!mayWork(permission, isMember)) return fail(DENIED.edit);

      const slot = uploadSlot(file);
      const before = await readAll(endpoint, T.attachments);
      const beforeIds = new Set(before.map((a) => String(a.id)));

      const res = await addData(endpoint, T.attachments, {
        ...pick(T.attachments, {
          task_id: payload.task_id,
          subtask_id: String(payload.subtask_id || "0"),
          comment_id: String(payload.comment_id || "0"),
          staff_id: me,
          file_name: file.name,
          file_path: UPLOAD_BASE[slot],
          file_type: file.type || "",
          file_size: file.size,
        }),
        // The File itself, under the key PHP looks for.
        [slot]: file,
      });

      if (!res?.success) return fail(res?.message || "Upload failed");

      const rows = Array.isArray(res.data) ? res.data : [];
      const added = rows.filter(
        (r) => !beforeIds.has(String(r.id)) && same(r.task_id, payload.task_id)
      );
      const row = added.length
        ? added.reduce((a, b) => (Number(b.id) > Number(a.id) ? b : a))
        : null;

      return row
        ? ok({ id: row.id }, "File attached")
        : fail("Uploaded, but the attachment could not be located");
    },
    [readAll, addData, permissionForTask, me]
  );

  const moveTask = useCallback(
    async (endpoint, payload) => {
      if (!payload.id) return fail("No task given");

      const { permission, isMember } = await permissionForTask(endpoint, payload.id);
      if (!mayWork(permission, isMember)) return fail(DENIED.edit);

      const changes = {};
      if (payload.folder_id !== undefined) {
        // 0 is "Unfiled". It is a real value, not a blank, so the CRUD
        // will write it - which is exactly why the column is NOT NULL
        // DEFAULT 0 rather than nullable.
        changes.folder_id = String(payload.folder_id || "0");
      }

      if (payload.status) {
        const subtasks = await readAll(endpoint, T.subtasks);
        const mine = subtasks.filter((s) => same(s.task_id, payload.id));
        if (mine.length > 0) {
          return fail(
            "This task has subtasks - its stage follows them automatically"
          );
        }
        changes.status = payload.status;
      }

      if (!Object.keys(changes).length) return fail("Nothing to change");

      const res = await updateData(endpoint, T.tasks, changes, payload.id);
      return res?.success ? ok(null, "Task updated") : fail(res?.message || "Could not move the task");
    },
    [readAll, updateData, permissionForTask]
  );

  const logTaskProgress = useCallback(
    async (endpoint, payload) => {
      if (!payload.task_id) return fail("No task given");
      if (!String(payload.work_done || "").trim()) {
        return fail("Describe what you worked on");
      }

      // Logging progress on someone else's board needs full rights,
      // unless you are on the task - then it is your own work.
      const { permission, isMember } = await permissionForTask(endpoint, payload.task_id);
      if (!mayManage(permission, isMember)) return fail(DENIED.full);

      const subtaskId = String(payload.subtask_id || "0");
      const logDate = payload.log_date;

      const rows = await readAll(endpoint, T.progress);
      const existing = rows.find(
        (p) =>
          same(p.task_id, payload.task_id) &&
          same(p.staff_id, me) &&
          same(p.subtask_id ?? "0", subtaskId) &&
          same(p.log_date, logDate)
      );

      const body = { ...payload, subtask_id: subtaskId, staff_id: me };

      if (existing) {
        const res = await updateData(
          endpoint,
          T.progress,
          pick(T.progress, body),
          existing.id
        );
        return res?.success
          ? ok({ id: existing.id }, "Progress updated")
          : fail(res?.message || "Could not update your progress");
      }

      const { row, message } = await insertAndFind(
        endpoint,
        T.progress,
        body,
        (r) =>
          same(r.task_id, payload.task_id) &&
          same(r.staff_id, me) &&
          same(r.log_date, logDate) &&
          same(r.subtask_id ?? "0", subtaskId)
      );
      return row ? ok({ id: row.id }, "Progress recorded") : fail(message);
    },
    [readAll, insertAndFind, updateData, permissionForTask, me]
  );

  const saveTaskFolder = useCallback(
    async (endpoint, payload) => {
      const ownerId = payload.owner_id || me;
      const name = String(payload.name || "").trim();
      if (!name) return fail("Folder name is required");

      const { permission } = await resolveAccess(endpoint, ownerId);
      if (!atLeast(permission, "full")) {
        return fail("You need full rights to change folders here");
      }

      const parentId = String(payload.parent_id || "0");
      const folders = await readAll(endpoint, T.folders);

      const clash = folders.find(
        (f) =>
          same(f.owner_id, ownerId) &&
          same(f.parent_id ?? "0", parentId) &&
          String(f.name).toLowerCase() === name.toLowerCase() &&
          !same(f.id, payload.id)
      );
      if (clash) return fail(`There is already a folder called "${name}" here`);

      /* ---- rename / move ---- */
      if (payload.id) {
        if (same(payload.id, parentId)) return fail("A folder cannot sit inside itself");

        // Walk up from the new parent; if we meet this folder we would
        // create a loop and the sidebar would recurse forever.
        let cursor = parentId;
        const guard = new Set();
        while (cursor && cursor !== "0" && !guard.has(cursor)) {
          if (same(cursor, payload.id)) {
            return fail("That would put the folder inside one of its own subfolders");
          }
          guard.add(cursor);
          const next = folders.find((f) => same(f.id, cursor));
          cursor = next ? String(next.parent_id ?? "0") : "0";
        }

        const res = await updateData(
          endpoint,
          T.folders,
          { name, parent_id: parentId },
          payload.id
        );
        return res?.success
          ? ok({ id: payload.id }, "Folder renamed")
          : fail(res?.message || "Could not save the folder");
      }

      /* ---- create ---- */
      const siblings = folders.filter(
        (f) => same(f.owner_id, ownerId) && same(f.parent_id ?? "0", parentId)
      );
      const sortOrder =
        payload.sort_order !== undefined && payload.sort_order !== ""
          ? payload.sort_order
          : siblings.length + 1;

      const { row, message } = await insertAndFind(
        endpoint,
        T.folders,
        { ...payload, owner_id: ownerId, parent_id: parentId, name, sort_order: sortOrder, created_by: me },
        (r) =>
          same(r.owner_id, ownerId) &&
          same(r.parent_id ?? "0", parentId) &&
          String(r.name).toLowerCase() === name.toLowerCase()
      );
      return row ? ok({ id: row.id }, "Folder created") : fail(message);
    },
    [readAll, resolveAccess, insertAndFind, updateData, me]
  );

  const shareTaskAccount = useCallback(
    async (endpoint, payload) => {
      const granteeId = payload.grantee_id;
      const permission = payload.permission || "view";
      if (!granteeId) return fail("Choose a staff member");
      if (same(granteeId, me)) return fail("You already have full rights to your own account");
      if (!RANK[permission]) return fail("Unknown permission");

      const access = await readAll(endpoint, T.access);
      const existing = access.find(
        (a) => same(a.owner_id, me) && same(a.grantee_id, granteeId)
      );

      if (existing) {
        const res = await updateData(endpoint, T.access, { permission }, existing.id);
        return res?.success
          ? ok({ id: existing.id }, "Access updated")
          : fail(res?.message || "Could not update access");
      }

      const { row, message } = await insertAndFind(
        endpoint,
        T.access,
        { owner_id: me, grantee_id: granteeId, permission, granted_by: me },
        (r) => same(r.owner_id, me) && same(r.grantee_id, granteeId)
      );
      return row ? ok({ id: row.id }, "Access granted") : fail(message);
    },
    [readAll, insertAndFind, updateData, me]
  );

  const revokeTaskAccess = useCallback(
    async (endpoint, payload) => {
      // Only the owner of an account may take access away from it.
      if (!same(payload.owner_id, me)) {
        return fail("Only the account owner can remove access");
      }

      const access = await readAll(endpoint, T.access);
      const row = access.find(
        (a) => same(a.owner_id, me) && same(a.grantee_id, payload.grantee_id)
      );
      if (!row) return fail("That person does not have access");

      const res = await deleteData(endpoint, T.access, row.id);
      return res?.success
        ? ok(null, "Access removed")
        : fail(res?.message || "Could not remove access");
    },
    [readAll, deleteData, me]
  );

  const removeTaskMember = useCallback(
    async (endpoint, payload) => {
      const { permission } = await permissionForTask(endpoint, payload.task_id);
      if (!atLeast(permission, "full")) return fail(DENIED.full);

      const members = await readAll(endpoint, T.members);
      const row = members.find(
        (m) => same(m.task_id, payload.task_id) && same(m.staff_id, payload.staff_id)
      );
      if (!row) return fail("That person is not on this task");

      const res = await deleteData(endpoint, T.members, row.id);
      return res?.success
        ? ok(null, "Removed from the task")
        : fail(res?.message || "Could not remove them");
    },
    [readAll, deleteData, permissionForTask]
  );

  /**
   * Children first. There are no ON DELETE CASCADE rules to lean on here,
   * and deleteData refuses a row that is still referenced - so clearing the
   * task out in the wrong order just fails half way.
   */
  const deleteTask = useCallback(
    async (endpoint, payload) => {
      const taskId = payload.id;
      if (!taskId) return fail("No task given");

      // Deleting is destructive, so it sits with create at full rights.
      const { permission } = await permissionForTask(endpoint, taskId);
      if (!atLeast(permission, "full")) return fail(DENIED.full);

      const [subtasks, members, progress] = await Promise.all([
        readAll(endpoint, T.subtasks),
        readAll(endpoint, T.members),
        readAll(endpoint, T.progress),
      ]);

      const mySubtasks = subtasks.filter((s) => same(s.task_id, taskId));
      const subtaskIds = new Set(mySubtasks.map((s) => String(s.id)));

      const subtaskMembers = subtaskIds.size
        ? await readAll(endpoint, T.subtaskMembers)
        : [];

      for (const sm of subtaskMembers.filter((r) => subtaskIds.has(String(r.subtask_id)))) {
        await deleteData(endpoint, T.subtaskMembers, sm.id);
      }
      for (const p of progress.filter((r) => same(r.task_id, taskId))) {
        await deleteData(endpoint, T.progress, p.id);
      }
      for (const s of mySubtasks) {
        await deleteData(endpoint, T.subtasks, s.id);
      }
      for (const m of members.filter((r) => same(r.task_id, taskId))) {
        await deleteData(endpoint, T.members, m.id);
      }

      const res = await deleteData(endpoint, T.tasks, taskId);
      return res?.success
        ? ok(null, "Task deleted")
        : fail(res?.message || "Could not delete the task");
    },
    [readAll, deleteData, permissionForTask]
  );

  const deleteSubtask = useCallback(
    async (endpoint, payload) => {
      const subtaskId = payload.id;
      if (!subtaskId) return fail("No subtask given");

      const allSubtasks = await readAll(endpoint, T.subtasks);
      const parentId = allSubtasks.find((s) => same(s.id, subtaskId))?.task_id;
      const { permission } = await permissionForTask(endpoint, parentId);
      if (!atLeast(permission, "full")) return fail(DENIED.full);

      const [subtaskMembers, progress] = await Promise.all([
        readAll(endpoint, T.subtaskMembers),
        readAll(endpoint, T.progress),
      ]);

      for (const sm of subtaskMembers.filter((r) => same(r.subtask_id, subtaskId))) {
        await deleteData(endpoint, T.subtaskMembers, sm.id);
      }
      for (const p of progress.filter((r) => same(r.subtask_id, subtaskId))) {
        await deleteData(endpoint, T.progress, p.id);
      }

      const res = await deleteData(endpoint, T.subtasks, subtaskId);
      return res?.success
        ? ok(null, "Subtask deleted")
        : fail(res?.message || "Could not delete the subtask");
    },
    [readAll, deleteData, permissionForTask]
  );

  const deleteTaskFolder = useCallback(
    async (endpoint, payload) => {
      const folderId = payload.id;
      if (!folderId) return fail("No folder given");

      const folders = await readAll(endpoint, T.folders);
      const folder = folders.find((f) => same(f.id, folderId));
      if (!folder) return fail("That folder no longer exists");

      const { permission } = await resolveAccess(endpoint, folder.owner_id);
      if (!atLeast(permission, "full")) {
        return fail("You need full rights to delete folders here");
      }

      const children = folders.filter((f) => same(f.parent_id ?? "0", folderId));
      if (children.length) {
        return fail("Empty or move the subfolders first");
      }

      // Tasks are not deleted with the folder - they fall back to Unfiled.
      const tasks = await readAll(endpoint, T.tasks);
      for (const t of tasks.filter((r) => same(r.folder_id, folderId))) {
        await updateData(endpoint, T.tasks, { folder_id: "0" }, t.id);
      }

      const res = await deleteData(endpoint, T.folders, folderId);
      return res?.success
        ? ok(null, "Folder deleted - its tasks moved to Unfiled")
        : fail(res?.message || "Could not delete the folder");
    },
    [readAll, resolveAccess, updateData, deleteData]
  );

  /* ==================================================================== */

  return useMemo(() => {
    const handlers = {
      getTaskAccounts,
      getTaskWorkspace,
      getTaskDetail,
      getTaskAccessList,
      saveTask,
      saveTaskMember,
      saveSubtask,
      saveTaskComment,
      saveTaskAttachment,
      moveTask,
      logTaskProgress,
      saveTaskFolder,
      shareTaskAccount,
      revokeTaskAccess,
      removeTaskMember,
      deleteTask,
      deleteSubtask,
      deleteTaskFolder,
    };

    /**
     * Same shape as the old AuthContext.callAction, so the components do
     * not care that the work now happens in the browser.
     */
    return async (endpoint, action, payload = {}) => {
      // The board also asks for plain tables (the staff picker). Those go
      // straight through to getData.
      if (action === "getDatas") {
        return getData(endpoint, payload.table, payload.id ?? null);
      }

      const handler = handlers[action];
      if (!handler) return fail(`Unknown task action: ${action}`);

      try {
        return await handler(endpoint, payload);
      } catch (err) {
        console.error(`taskApi ${action} failed`, err);
        return fail(err?.message || "Something went wrong");
      }
    };
  }, [
    getData,
    getTaskAccounts,
    getTaskWorkspace,
    getTaskDetail,
    getTaskAccessList,
    saveTask,
    saveTaskMember,
    saveSubtask,
    saveTaskComment,
    saveTaskAttachment,
    moveTask,
    logTaskProgress,
    saveTaskFolder,
    shareTaskAccount,
    revokeTaskAccess,
    removeTaskMember,
    deleteTask,
    deleteSubtask,
    deleteTaskFolder,
  ]);
};

export default useTaskApi;
